/**
 * ParticipantStepDetail - Step detail panel for Participant Workspace
 *
 * Shows step details, allows adding notes/links, and marking completion.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Chip,
  IconButton,
  Collapse,
  Alert,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LinkIcon from '@mui/icons-material/Link';
import NotesIcon from '@mui/icons-material/Notes';
import { colors } from '../../theme/colors';

interface StepBlockedInfo {
  isBlocked: boolean;
  hiddenPrereqCount: number;
  visiblePrereqIds: string[];
  blockedReason: string | null;
  canStart: boolean;
}

interface StepContext {
  notes: string | null;
  links: string[] | null;
  lastUpdatedAt: string | null;
  collectedData: any | null;
}

interface ParticipantStep {
  stepId: string;
  title: string;
  description: string | null;
  status: string;
  phaseLabel: string | null;
  blocked: StepBlockedInfo;
  context: StepContext | null;
  assignedViaRole: string | null;
}

interface ParticipantStepDetailProps {
  step: ParticipantStep;
  onToggleCompletion: (stepId: string, completed: boolean) => void;
  onUpdateContext: (stepId: string, context: { notes?: string; links?: string[] }) => void;
  isUpdating?: boolean;
}

export const ParticipantStepDetail: React.FC<ParticipantStepDetailProps> = ({
  step,
  onToggleCompletion,
  onUpdateContext,
  isUpdating = false,
}) => {
  const [notes, setNotes] = useState(step.context?.notes || '');
  const [newLink, setNewLink] = useState('');
  const [links, setLinks] = useState<string[]>(step.context?.links || []);
  const [showDescription, setShowDescription] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  // Reset state when step changes
  useEffect(() => {
    setNotes(step.context?.notes || '');
    setLinks(step.context?.links || []);
    setHasChanges(false);
  }, [step.stepId, step.context?.notes, step.context?.links]);

  const isCompleted = step.status === 'COMPLETED';
  const canStart = step.blocked.canStart;
  const isBlocked = step.blocked.isBlocked;

  const handleNotesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNotes(e.target.value);
    setHasChanges(true);
  };

  const handleAddLink = () => {
    if (newLink.trim()) {
      const updatedLinks = [...links, newLink.trim()];
      setLinks(updatedLinks);
      setNewLink('');
      setHasChanges(true);
    }
  };

  const handleRemoveLink = (index: number) => {
    const updatedLinks = links.filter((_, i) => i !== index);
    setLinks(updatedLinks);
    setHasChanges(true);
  };

  const handleSaveContext = () => {
    onUpdateContext(step.stepId, { notes, links });
    setHasChanges(false);
  };

  const handleToggleCompletion = () => {
    onToggleCompletion(step.stepId, !isCompleted);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: `1px solid ${colors.border}`,
        borderRadius: 2,
        bgcolor: colors.card,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        {/* Status icon */}
        <Box sx={{ pt: 0.5 }}>
          {isCompleted ? (
            <CheckCircleIcon sx={{ fontSize: 28, color: colors.green[600] }} />
          ) : canStart ? (
            <PlayArrowIcon sx={{ fontSize: 28, color: colors.blue[500] }} />
          ) : (
            <HourglassEmptyIcon sx={{ fontSize: 28, color: colors.amber[500] }} />
          )}
        </Box>

        {/* Title and metadata */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: isCompleted ? colors.textSecondary : colors.textPrimary,
              textDecoration: isCompleted ? 'line-through' : 'none',
              mb: 0.5,
            }}
          >
            {step.title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {step.phaseLabel && (
              <Chip
                label={step.phaseLabel}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  bgcolor: colors.gray[100],
                  color: colors.textSecondary,
                }}
              />
            )}
            {step.assignedViaRole && (
              <Chip
                label={`Role: ${step.assignedViaRole}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  bgcolor: colors.purple[50],
                  color: colors.purple[600],
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Blocked warning */}
      {isBlocked && !isCompleted && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          icon={<HourglassEmptyIcon />}
        >
          {step.blocked.blockedReason || 'This task is waiting on other tasks to complete.'}
        </Alert>
      )}

      {/* Description */}
      {step.description && (
        <Box sx={{ mb: 2 }}>
          <Box
            onClick={() => setShowDescription(!showDescription)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ color: colors.textSecondary, fontWeight: 600 }}>
              Description
            </Typography>
            <IconButton size="small">
              {showDescription ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
          <Collapse in={showDescription}>
            <Typography
              variant="body2"
              sx={{
                color: colors.textSecondary,
                whiteSpace: 'pre-wrap',
                bgcolor: colors.gray[50],
                p: 2,
                borderRadius: 1,
              }}
            >
              {step.description}
            </Typography>
          </Collapse>
        </Box>
      )}

      {/* Notes section */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <NotesIcon sx={{ fontSize: 16, color: colors.textSecondary }} />
          <Typography variant="subtitle2" sx={{ color: colors.textSecondary, fontWeight: 600 }}>
            Notes
          </Typography>
        </Box>
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Add notes about your progress..."
          value={notes}
          onChange={handleNotesChange}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem',
            },
          }}
        />
      </Box>

      {/* Links section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <LinkIcon sx={{ fontSize: 16, color: colors.textSecondary }} />
          <Typography variant="subtitle2" sx={{ color: colors.textSecondary, fontWeight: 600 }}>
            Links
          </Typography>
        </Box>
        {links.length > 0 && (
          <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {links.map((link, index) => (
              <Chip
                key={index}
                label={link}
                size="small"
                onDelete={() => handleRemoveLink(index)}
                sx={{
                  maxWidth: '100%',
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
                component="a"
                href={link.startsWith('http') ? link : `https://${link}`}
                target="_blank"
                clickable
              />
            ))}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Add a link..."
            value={newLink}
            onChange={(e) => setNewLink(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddLink()}
            sx={{ flex: 1, '& .MuiOutlinedInput-root': { fontSize: '0.875rem' } }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={handleAddLink}
            disabled={!newLink.trim()}
          >
            Add
          </Button>
        </Box>
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {hasChanges && (
          <Button
            variant="outlined"
            onClick={handleSaveContext}
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={16} /> : null}
          >
            Save Notes
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleToggleCompletion}
          disabled={isUpdating || (isBlocked && !isCompleted)}
          startIcon={
            isUpdating ? (
              <CircularProgress size={16} color="inherit" />
            ) : isCompleted ? (
              <CheckCircleOutlineIcon />
            ) : (
              <CheckCircleIcon />
            )
          }
          sx={{
            bgcolor: isCompleted ? colors.gray[500] : colors.green[600],
            '&:hover': {
              bgcolor: isCompleted ? colors.gray[600] : colors.green[700],
            },
          }}
        >
          {isCompleted ? 'Mark Incomplete' : 'Mark Complete'}
        </Button>
      </Box>
    </Paper>
  );
};

export default ParticipantStepDetail;
